import { Draft } from "immer";
import { getUser } from "utils/authentication";
import { randomArchivingAddress } from "../Steps/Target/ArchivingAddress/utils";

import {
  ApprovalSteps,
  ArchivingAddress,
  Content,
  EmailFlow,
  Mailbox,
  Storage,
} from "@dashboard-v3/api";
import { Rule } from "pages/Rules/common/types";
import { State, Step } from "./types";
import { StorageWebhookUpdate } from "./reducers/updateStep";
import {
  typePath,
  getCloudStorage,
  getArchivingAddress,
  getEmailFlow,
  getMailbox,
  getStorageWebhook,
} from "./ruleSelectors";

export function needToConfirm(state: State, step: Step): boolean {
  return !state.steps[step].done;
}

export const updateApprovalSteps = (
  rule: Draft<Rule>,
  changes: Partial<ApprovalSteps>
): ApprovalSteps => {
  const path = typePath(rule.actionType, rule);

  if ("expiration" in changes) {
    path["expiration"] = changes.expiration;
  }

  if ("supervisors" in changes) {
    path["supervisors"] = changes.supervisors;
  }

  if ("supervisorNotification" in changes) {
    if (changes.supervisorNotification === null) {
      delete path["supervisorNotification"];
    } else {
      path["supervisorNotification"] = changes.supervisorNotification;
    }
  }

  if ("senderNotification" in changes) {
    if (changes.senderNotification === null) {
      delete path["senderNotification"];
    } else {
      path["senderNotification"] = changes.senderNotification;
    }
  }

  if ("doneNotification" in changes) {
    if (changes.doneNotification === null) {
      delete path["doneNotification"];
    } else {
      path["doneNotification"] = changes.doneNotification;
    }
  }

  if ("reminderSenderNotification" in changes) {
    if (changes.reminderSenderNotification === null) {
      delete path["reminderSenderNotification"];
    } else {
      path["reminderSenderNotification"] = changes.reminderSenderNotification;
    }
  }

  if ("reminder" in changes) {
    if (changes.reminder === null) {
      delete path["reminder"];
    } else {
      path["reminder"] = changes.reminder;
    }
  }

  return path as ApprovalSteps;
};

export const updateCloudStorage = (
  rule: Draft<Rule>,
  changes: Partial<Storage>
): Storage => {
  const path = typePath(rule.actionType, rule);
  path["storage"] = {
    ...getCloudStorage(rule),
    ...changes,
  };

  return path["storage"];
};

export const updateArchivingAddress = (
  rule: Draft<Rule>,
  changes: Partial<ArchivingAddress>
): Draft<ArchivingAddress> => {
  const path = typePath(rule.actionType, rule);
  path["archivingAddress"] = {
    ...getArchivingAddress(rule),
    ...changes,
  };

  return path["archivingAddress"];
};

export const updateEmailFlow = (
  rule: Draft<Rule>,
  changes: Partial<EmailFlow> | { saveForwardedMessages: boolean }
) => {
  const path = typePath(rule.actionType, rule);

  // https://mxhero.atlassian.net/browse/MXH-4513
  if ("saveForwardedMessages" in changes) {
    rule.copyEmails.saveForwardedMessages = changes.saveForwardedMessages;
    return path["emailFlow"];
  }

  path["emailFlow"] = {
    ...getEmailFlow(rule),
    ...changes,
  };

  return path["emailFlow"];
};

export const updateMailbox = (
  rule: Draft<Rule>,
  changes: Partial<Mailbox>
): Draft<Mailbox> => {
  const path = typePath(rule.actionType, rule);
  path["mailbox"] = {
    ...getMailbox(rule),
    ...changes,
  };

  return path["mailbox"];
};

export const updateWebhook = (
  state: Draft<State>,
  changes: Partial<StorageWebhookUpdate>
) => {
  const path = typePath(state.rule.actionType, state.rule);
  const { webhookStatus, ...webhookChanges } = changes;

  state.webhookStatus = {
    isValid: webhookStatus?.isValid || false,
    foldersInUse: webhookStatus?.foldersInUse || [],
    missingFolder: webhookStatus?.missingFolder || "",
    userWithoutAccess: webhookStatus?.errorType === "user_without_access",
  };

  path["storageWebhook"] = {
    ...getStorageWebhook(state.rule),
    ...webhookChanges,
  };

  if (!path["storageWebhook"].folders.length) {
    path["storageWebhook"].account = "";
  }

  return path["storageWebhook"];
};

export const getInitArchivingAddress = (): Partial<ArchivingAddress> => {
  const { email: redirectAddress } = getUser();

  return {
    emailAddress: randomArchivingAddress(),
    redirectOnErrorEmailAddress: redirectAddress,
    saveForwardedMessages: false,
  };
};

export const getInitEmailFlow = (rule?: Rule): Partial<EmailFlow> => {
  const isRequestApproval = rule?.actionType === "REQUEST_APPROVAL";
  const isSendDelay = rule?.actionType === "SEND_DELAY";

  const initEmailFlow: Partial<EmailFlow> = {
    fromTo: {
      fromDirection: { type: "ORGANIZATION" },
      toDirection: { type: "ANYONE" },
    },
    smtpPhase: false,
    evaluateHeaders: false,
    bidirectional: true,
  };

  if (isRequestApproval || isSendDelay) {
    initEmailFlow.smtpPhase = true;
    initEmailFlow.bidirectional = false;
    initEmailFlow.evaluateHeaders = true;
    initEmailFlow.evaluateFrom = false;
  }

  if (rule?.actionType === "ZIP_ATTACHMENTS") {
    initEmailFlow.bidirectional = false;
    initEmailFlow.smtpPhase = false;
  }

  return initEmailFlow;
};

export function canRestart(rule: Rule) {
  if (rule.targetType === "MAIL_BOX") {
    const mailbox = getMailbox(rule);
    return mailbox?.canRestart ?? true;
  }
  return true;
}

export const isWebhookProcessingUpdate = (state: State) => {
  if (state.webhookProcessing) {
    const { hasChanged } = state.webhookProcessing;
    return hasChanged;
  }
  return false;
};

export function isCopyFilesAllowed(
  content: Content[],
  ruleType: Rule["actionType"]
) {
  const contentTypes = content.map(ct => ct.type);
  const allowedContent = ["PDF", "EML"] as Partial<Content["type"]>[];

  return (
    ruleType === "REPLACE_ATTACHMENTS" &&
    allowedContent.some(t => contentTypes.includes(t))
  );
}

export const ruleTypeMap = new Map([
  ["COPY_EMAILS", "copyEmails"],
  ["MOVE_EMAILS", "moveEmails"],
  ["PROTECT_EMAILS", "protectEmails"],
  ["REPLACE_ATTACHMENTS", "replaceAttachments"],
  ["BLOCK_ATTACHMENTS", "blockAttachments"],
  ["DRAG_AND_DROP", "dragAndDrop"],
  ["ZIP_ATTACHMENTS", "zipAttachments"],
]);
